import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { callApi } from "../../api/apiCaller";
import { BASE_URL, routes } from "../../api/routes";
import { GreenNotify, RedNotify } from "../../helper/helper";
import { Col, Input, Row, Select, Tag } from "antd";
import Loader from "../loader/loader";

const { Option } = Select;
const MAX_SELECTED_BANKS = 3;

const normalizeEmployerIds = (employers = []) => {
  if (!Array.isArray(employers)) {
    return [];
  }

  return employers
    .map((employer) => {
      if (employer && typeof employer === "object") {
        return employer._id || employer.id || "";
      }

      return employer || "";
    })
    .filter(Boolean);
};

const EditUser = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const dummyImage =
    "https://novathreadbucket.s3.amazonaws.com/nova-app-1685176470232-dummy.PNG";
  
  const [name, setName] = useState(location?.state?.item?.name || "");
  const [email, setEmail] = useState(location?.state?.item?.email || "");
  const [number, setNumber] = useState(location?.state?.item?.number || "");
  const [image, setImage] = useState(location?.state?.item?.image || "");
  const [subscriptionPlan] = useState(location?.state?.item?.subscriptionPlan || "free");
  const [employers, setEmployers] = useState([]);
  const [selectedEmployers, setSelectedEmployers] = useState(
    normalizeEmployerIds(location?.state?.item?.employer || [])
  );

  const [isloading, setIsLoading] = useState(false);
  const id = location?.state?.item?._id;
  const fileInputImageRef = useRef(null);
  const canEditEmail = !String(location?.state?.item?.email || "").trim();
  const canEditNumber = !String(location?.state?.item?.number || "").trim();

  const selectedEmployerDetails = employers.filter((employer) =>
    selectedEmployers.includes(employer._id)
  );

  useEffect(() => {
    const getEmployers = () => {
      const onSuccess = (res) => {
        setEmployers(res?.data?.data || []);
      };

      callApi(
        "GET",
        routes.getAllEmployers,
        null,
        setIsLoading,
        onSuccess,
        (error) => {
          console.log("error", error);
          RedNotify(error?.message || "Failed to load banks");
        }
      );
    };

    getEmployers();
  }, []);

  const pickImageFile = () => {
    fileInputImageRef.current.click();
  };

  const handleImageUpload = (evt) => {
    const files = evt.target.files;
    const file = files[0];
    
    if (!file) {
      // Reset input to allow same file selection
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      RedNotify("Invalid file type. Please select JPG, PNG, or GIF image.");
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Validate file size (Max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      RedNotify("File size is too large. Maximum file size is 5MB.");
      if (fileInputImageRef.current) {
        fileInputImageRef.current.value = '';
      }
      return;
    }

    // Create custom upload handler with proper error handling
    setIsLoading(true);
    
    var formdata = new FormData();
    formdata.append("file", file);

    var requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };

    fetch(`${BASE_URL}api/v1/user/upload`, requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.url) {
          setImage(data.url);
          GreenNotify("Image uploaded successfully!");
          setIsLoading(false);
        } else {
          throw new Error("Upload failed: No URL received from server");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Upload error:", error);
        RedNotify(error.message || "Failed to upload image. Please try again.");
      })
      .finally(() => {
        // Reset input value to allow selecting the same file again
        setTimeout(() => {
          if (fileInputImageRef.current) {
            fileInputImageRef.current.value = '';
          }
        }, 100);
    });
  };

  const handleEmployerChange = (values) => {
    if (values.length > MAX_SELECTED_BANKS) {
      RedNotify(`You can select up to ${MAX_SELECTED_BANKS} banks only.`);
      return;
    }

    setSelectedEmployers(values);
  };

  const renderEmployerTag = (tagProps) => {
    const { label, value, closable, onClose } = tagProps;
    const employer = employers.find((item) => item._id === value);

    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginRight: 4,
          borderRadius: "999px",
          padding: "4px 10px",
          background: "#f7f9fc",
          border: "1px solid #dbe4f0",
        }}
      >
        <img
          src={employer?.image || dummyImage}
          alt={employer?.name || label}
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.currentTarget.src = dummyImage;
          }}
        />
        <span>{employer?.name || label}</span>
      </Tag>
    );
  };

  const UpdateUser = () => {
    if (selectedEmployers.length > MAX_SELECTED_BANKS) {
      RedNotify(`You can select up to ${MAX_SELECTED_BANKS} banks only.`);
      return;
    }

    if (canEditEmail && !email.trim()) {
      RedNotify("Please enter email address");
      return;
    }

    if (canEditNumber && !String(number || "").trim()) {
      RedNotify("Please enter phone number");
      return;
    }

    let getRes = (res) => {
      console.log("res of updateUser", res);
      GreenNotify("User is updated successfully");
      navigate("/users");
    };

    let body = {
      name: name,
      email: email,
      number: number,
      image: image,
      subscriptionPlan: subscriptionPlan,
      employer: selectedEmployers,
      isPreferencesSet: selectedEmployers.length > 0,
      isPreferencesSkipped: false,
    };

    callApi(
      "PATCH",
      `${routes.updateUser}${id}`,
      body,
      setIsLoading,
      getRes,
      (error) => {
        console.log("error", error);
      }
    );
  };

  return (
    <div className="modern-page-container">
      <Loader loading={isloading} />
      
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1 className="page-title">Edit User</h1>
            <p className="page-subtitle">Update user information and bank preferences</p>
          </div>
          <button
            onClick={() => navigate("/users")}
            className="modern-back-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Users
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <div className="form-header">
          <h2 className="form-title">User Details</h2>
          <p className="form-description">Update the fields below to edit user information and preferences</p>
        </div>

        <div className="form-content">
          <Row gutter={[32, 24]}>
            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  User Name
                  <span className="required-star">*</span>
                </label>
                <Input
                  placeholder="Enter user name"
                  className="modern-input"
                  size="large"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Email
                </label>
                <Input
                  placeholder="Enter email address"
                  className="modern-input"
                  size="large"
                  type="email"
                  value={email}
                  disabled={!canEditEmail}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    backgroundColor: canEditEmail ? '#ffffff' : '#f5f5f5',
                    cursor: canEditEmail ? 'text' : 'not-allowed'
                  }}
                />
                {canEditEmail && (
                  <p className="image-help-text">
                    Email was empty, so it can be updated here.
                  </p>
                )}
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M22 16.92V19.92C22 20.4504 21.7893 20.9601 21.4142 21.3352C21.0391 21.7103 20.5304 21.92 20 21.92H4C3.46957 21.92 2.96086 21.7103 2.58579 21.3352C2.21071 20.9601 2 20.4504 2 19.92V16.92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 10.92L12 15.92L17 10.92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 2.08V15.92" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Phone Number
                </label>
                <Input
                  placeholder="Enter phone number"
                  className="modern-input"
                  size="large"
                  value={number}
                  disabled={!canEditNumber}
                  onChange={(e) => setNumber(e.target.value)}
                  style={{
                    backgroundColor: canEditNumber ? '#ffffff' : '#f5f5f5',
                    cursor: canEditNumber ? 'text' : 'not-allowed'
                  }}
                />
                {canEditNumber && (
                  <p className="image-help-text">
                    Phone number was empty, so it can be updated here.
                  </p>
                )}
              </div>
            </Col>

            {/*
            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Subscription Plan
                </label>
                <Select
                  placeholder="Select subscription plan"
                  className="modern-select subscription-select"
                  size="large"
                  style={{ width: "100%" }}
                  value={subscriptionPlan}
                  disabled={true}
                >
                  <Option value="free">Free</Option>
                  <Option value="premium">Premium</Option>
                  <Option value="not-subscribed">Not Subscribed</Option>
                </Select>
              </div>
            </Col>
            */}

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <path d="M3 7H21M6 7V5C6 3.89543 6.89543 3 8 3H16C17.1046 3 18 3.89543 18 5V7M5 7H19V20H5V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 11H15M9 15H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Bank Preferences
                </label>
                <Select
                  mode="multiple"
                  placeholder="Select up to 3 banks"
                  className="modern-select employer-select"
                  size="large"
                  style={{ width: "100%" }}
                  value={selectedEmployers}
                  onChange={handleEmployerChange}
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  tagRender={renderEmployerTag}
                  disabled={isloading}
                >
                  {employers.map((employer) => (
                    <Option key={employer._id} value={employer._id} label={employer.name}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <img
                          src={employer.image || dummyImage}
                          alt={employer.name}
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "2px solid #e2e8f0"
                          }}
                          onError={(e) => {
                            e.currentTarget.src = dummyImage;
                          }}
                        />
                        <span>{employer.name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
                <p className="image-help-text">
                  Select up to 3 banks. Current selection: {selectedEmployers.length}/3
                </p>
                {selectedEmployerDetails.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
                    {selectedEmployerDetails.map((employer) => (
                      <Tag
                        key={employer._id}
                        color="blue"
                        style={{ marginInlineEnd: 0, borderRadius: "999px", padding: "4px 10px" }}
                      >
                        {employer.name}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-group">
                <label className="form-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Profile Image
                </label>
                <div className="modern-image-upload-container">
                  <div className="image-preview-wrapper" onClick={pickImageFile}>
                    <img
                      alt="User Profile"
                      src={image.length !== 0 ? image : dummyImage}
                      className="image-preview"
                    />
                    <div className="image-overlay">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 8L12 3L7 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p>Click to upload</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputImageRef}
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                    accept="image/*"
                  />
                  <p className="image-help-text">Supported formats: JPG, PNG, GIF (Max 5MB)</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className="form-footer">
          <button
            onClick={() => navigate("/users")}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={UpdateUser}
            className="submit-btn"
            disabled={!name}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Update User
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
